<template>
    <card-tabs selected-name="pipelines">
        <template #labels>
            <card-tab-label
                v-slot="{ bindings }"
                name="pipelines"
            >
                <text-decorator
                    :color="bindings?.isActive ? Color.brandPurple : Color.gray500"
                    :size="FontSize.sm"
                    bold
                >
                    Pijplijn
                </text-decorator>
                <chip-informative
                    v-if="canViewPotentialOrderValue"
                    small
                    :color="bindings?.isActive ? Color.brandPurple : Color.gray500"
                >
                    <template #prefix>
                        {{ leadCollection.count }}
                    </template>
                    <template #default>
                        {{ formatMoneyShort(leadValue) }}
                    </template>
                </chip-informative>
                <chip-informative
                    v-else
                    small
                    :color="bindings?.isActive ? Color.brandPurple : Color.gray500"
                >
                    <template #default>
                        {{ leadCollection.count }}
                    </template>
                </chip-informative>
            </card-tab-label>

            <card-tab-label
                v-if="currentLegacyCompany?.orderSettings?.enabled"
                v-slot="{ bindings }"
                name="orders"
            >
                <text-decorator
                    :color="bindings?.isActive ? Color.brandPurple : Color.gray500"
                    :size="FontSize.sm"
                    bold
                >
                    Opdracht
                </text-decorator>
                <chip-informative
                    v-if="canViewPotentialOrderValue"
                    :color="bindings?.isActive ? Color.brandPurple : Color.gray500"
                    small
                >
                    <template #prefix>
                        {{ orderCount }}
                    </template>
                    <template #default>
                        {{ formatMoneyShort(orderValue) }}
                    </template>
                </chip-informative>
                <chip-informative
                    v-else
                    small
                    :color="bindings?.isActive ? Color.brandPurple : Color.gray500"
                >
                    <template #default>
                        {{ orderCount }}
                    </template>
                </chip-informative>
            </card-tab-label>
        </template>

        <template #panels>
            <card-tab-panel
                with-variable-height
                without-padding
                name="pipelines"
            >
                <pipeline-list />
            </card-tab-panel>

            <card-tab-panel
                v-if="currentLegacyCompany?.orderSettings?.enabled"
                with-variable-height
                without-padding
                name="orders"
            >
                <order-list />
            </card-tab-panel>
        </template>
    </card-tabs>
</template>

<script setup lang="ts">
    import {
        Color,
        CardTabs,
        CardTabLabel,
        CardTabPanel,
        ChipInformative,
        FontSize,
    } from '@keyvue/one-ui';

    import OrderList from '@views/Contact/Detail/Lists/OrderList.vue';
    import PipelineList from '@views/Contact/Detail/Lists/PipelineList.vue';
    import { computed, provide } from 'vue';
    import { Branch } from '@keyvue/one-api-client-legacy';
    import { formatMoneyShort } from '@/utils/money';
    import useClient from '@/composables/useClient';
    import useContactPageData from '@composables/useContactPageData';
    import { onBeforeRouteUpdate } from 'vue-router';

    interface Props {
        contactId: string;
    }

    const props = defineProps<Props>();
    const {
        currentLegacyCompany,
        currentLegacyUser,
        currentCompany,
    } = useClient();
    const contactPageData = useContactPageData(props.contactId);

    const [
        contact,
        leadCollection,
        orders,
    ] = await Promise.all([
        contactPageData.contact(),
        contactPageData.leadCollection(),
        contactPageData.orders(),
    ]);

    provide('contact', contact);
    provide('leadCollection', leadCollection);
    provide('orders', orders);

    const canViewPotentialOrderValue = computed(() => (
        currentLegacyCompany.value?.potentialOrderValueRoles.includes(currentLegacyUser.value?.role?.toString() ?? '')
    ));

    const valuableLeadCollection = computed(() => leadCollection.data.value.filter(
        (lead) => (lead.assignee.company.equals(currentCompany.value)),
    ));
    const leadValue = computed(() => valuableLeadCollection.value.reduce((acc, lead) => acc + (lead.potentialOrderValue ?? 0), 0));
    const orderCount = computed(() => orders.value.length);
    const valuableOrders = computed(() => orders.value.filter(
        (order) => {
            if (order.referral.assignedToBranch instanceof Branch) {
                return order.referral.assignedToBranch.company?.id === currentCompany.value?.id.value;
            }

            return false;
        },
    ));
    const orderValue = computed(() => valuableOrders.value.reduce((acc, order) => acc + (order.orderValue ?? 0), 0));

    onBeforeRouteUpdate((to) => {
        if (to.name !== 'contactDetail') {
            return;
        }

        // Not awaited: the shared list is updated in place when the reload is done.
        contactPageData.reloadLeadCollection();
    });
</script>
